import { Box, Chip, Grid, Stack, Typography, useTheme, alpha } from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import {
    AssignmentTurnedIn as AssignmentIcon,
    HowToReg as SelfSignUpIcon,
    EventAvailable as LeadTimeIcon,
    EventBusy as OpenSlotsIcon,
} from '@mui/icons-material';
import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import { themeConstants } from '@/theme';
import {
    MetricsStatCard,
    MetricsChartCard,
    MetricsSectionHeading,
    makeTooltipStyle,
} from './index';

interface Props {
    data: MetricsData;
}

export const MetricsAssignmentsSection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };
    const isFiltered = data.resourceId !== null;
    const filteredLabel = LL.metricsPage.filteredByUser();
    const signUps = data.assignments.signUps;
    const { openSlots, notServing } = data;
    const formatDay = (date: string) =>
        new Date(`${date}T00:00`).toLocaleDateString(undefined, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
        });
    const listSx = { height: '100%', overflowY: 'auto', pr: 1 } as const;
    const rowSx = { py: 0.75, borderBottom: 1, borderColor: 'divider' } as const;

    const assignmentsPerMonth = useMemo(
        () =>
            data.assignmentsPerMonth.map(({ month, count, crCreated, crCancelled, crAccepted }) => {
                const [y, m] = month.split('-');
                return {
                    month: new Date(Number(y), Number(m) - 1).toLocaleDateString(undefined, {
                        month: 'short',
                        year: '2-digit',
                    }),
                    count,
                    crCreated: crCreated ?? 0,
                    crCancelled: crCancelled ?? 0,
                    crAccepted: crAccepted ?? 0,
                };
            }),
        [data.assignmentsPerMonth]
    );

    return (
        <MetricsSectionHeading
            icon={<AssignmentIcon fontSize="small" />}
            title={LL.metricsPage.sections.assignments()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.totalAssignments()}
                        value={data.assignments.total}
                        icon={<AssignmentIcon />}
                        color="primary.main"
                        gradient={themeConstants.gradients.cardPrimary}
                        border={themeConstants.borders.primary}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.assignmentsInRange()}
                        value={data.assignments.inRange}
                        icon={<AssignmentIcon />}
                        color="success.main"
                        gradient={themeConstants.gradients.cardSuccess}
                        border={themeConstants.borders.success}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.openChangeRequests()}
                        value={data.assignments.openChangeRequests}
                        icon={<AssignmentIcon />}
                        color="warning.main"
                        gradient={themeConstants.gradients.cardWarning}
                        border={themeConstants.borders.warning}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                {openSlots.needed > 0 && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <MetricsStatCard
                            label={LL.metricsPage.stats.openSlots()}
                            value={openSlots.open}
                            suffix={`/ ${openSlots.needed}`}
                            icon={<OpenSlotsIcon />}
                            color="error.main"
                            gradient={`linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)} 0%, ${alpha(theme.palette.error.dark, 0.15)} 100%)`}
                            border={`1px solid ${alpha(theme.palette.error.main, 0.3)}`}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        />
                    </Grid>
                )}
                {/* From the assignment audit log; empty when auditing is off */}
                {signUps > 0 && (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <MetricsStatCard
                                label={LL.metricsPage.stats.selfSignUps()}
                                value={Math.round((data.assignments.selfSignUps / signUps) * 100)}
                                suffix="%"
                                icon={<SelfSignUpIcon />}
                                color="info.main"
                                gradient={`linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.dark, 0.15)} 100%)`}
                                border={`1px solid ${alpha(theme.palette.info.main, 0.3)}`}
                                filtered={isFiltered}
                                filteredLabel={filteredLabel}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <MetricsStatCard
                                label={LL.metricsPage.stats.avgLeadDays()}
                                value={data.assignments.avgLeadDays.toLocaleString()}
                                icon={<LeadTimeIcon />}
                                color="secondary.main"
                                gradient={themeConstants.gradients.cardSecondary}
                                border={themeConstants.borders.secondary}
                                filtered={isFiltered}
                                filteredLabel={filteredLabel}
                            />
                        </Grid>
                    </>
                )}

                {/* Assignments over time */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <MetricsChartCard
                        title={LL.metricsPage.charts.assignmentsPerMonth()}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={assignmentsPerMonth}
                                margin={{ left: 0, right: 16, top: 8 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis dataKey="month" tick={tickStyle} />
                                <YAxis allowDecimals={false} tick={tickStyle} />
                                <Tooltip {...tt} />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="count"
                                    stroke={theme.palette.primary.main}
                                    strokeWidth={2}
                                    dot={{ r: 4, fill: theme.palette.primary.main }}
                                    activeDot={{ r: 6 }}
                                    name={LL.metricsPage.charts.assignmentsLabel()}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="crCreated"
                                    stroke={theme.palette.warning.main}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    name={LL.metricsPage.charts.changeRequestsCreatedLabel()}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="crCancelled"
                                    stroke={theme.palette.error.main}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    name={LL.metricsPage.charts.changeRequestsCancelledLabel()}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="crAccepted"
                                    stroke={theme.palette.success.main}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    name={LL.metricsPage.charts.changeRequestsAcceptedLabel()}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* Top categories */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <MetricsChartCard
                        title={LL.metricsPage.charts.topCategoriesByAssignments()}
                        height={260}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data.assignmentsPerCategory}
                                layout="vertical"
                                margin={{ left: 8, right: 16 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis
                                    type="number"
                                    allowDecimals={false}
                                    tick={{ ...tickStyle, fontSize: 11 }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    tick={{ ...tickStyle, fontSize: 11 }}
                                    width={100}
                                />
                                <Tooltip {...tt} />
                                <Bar
                                    dataKey="count"
                                    fill={theme.palette.warning.main}
                                    radius={[0, 4, 4, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* Upcoming events still missing people */}
                {openSlots.events.length > 0 && (
                    <Grid size={{ xs: 12, md: isFiltered ? 12 : 6 }}>
                        <MetricsChartCard
                            title={LL.metricsPage.charts.openSlotsByEvent()}
                            filtered={isFiltered}
                            filteredLabel={filteredLabel}
                        >
                            <Box sx={listSx}>
                                {openSlots.events.map((ev) => (
                                    <Box key={ev.date} sx={rowSx}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatDay(ev.date)} · {ev.event}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
                                        >
                                            {ev.categories.map((c) => (
                                                <Chip
                                                    key={c.name}
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    label={
                                                        c.open > 1 ? `${c.name} ×${c.open}` : c.name
                                                    }
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                            </Box>
                        </MetricsChartCard>
                    </Grid>
                )}

                {/* Category members without an assignment in range (global only) */}
                {notServing.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <MetricsChartCard
                            title={`${LL.metricsPage.charts.notServing()} (${notServing.length})`}
                        >
                            <Box sx={listSx}>
                                {notServing.map((u, i) => (
                                    <Stack
                                        key={`${i}-${u.name}`}
                                        direction="row"
                                        sx={{ ...rowSx, justifyContent: 'space-between', gap: 2 }}
                                    >
                                        <Typography variant="body2">{u.name}</Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {u.lastServed
                                                ? LL.metricsPage.charts.lastServed({
                                                      date: new Date(
                                                          `${u.lastServed}T00:00`
                                                      ).toLocaleDateString(),
                                                  })
                                                : LL.metricsPage.charts.neverServed()}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                        </MetricsChartCard>
                    </Grid>
                )}
            </Grid>
        </MetricsSectionHeading>
    );
};
